import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Eye, EyeOff, Key } from "lucide-react";
import { toast } from "sonner";
import { updateUser } from "../../services/crmServices/userService";
import { useAuth } from "../../hooks/useAuth";

export function UpdatePasswordModal({ open, onClose, staff }) {
    const { user: currentUser } = useAuth();
    // Check if current user is an admin - passwords should be visible by default for admins
    const isAdmin = currentUser?.position?.role === "admin";
    
    const [formData, setFormData] = useState({
        newPassword: "",
        confirmPassword: ""
    });
    // For admins, show new passwords as visible by default; for others, keep them hidden
    // Initialize as false, will be set correctly by useEffect when modal opens
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    
    // Reset form and set visibility when modal opens/closes
    useEffect(() => {
        if (open) {
            // Reset form data
            setFormData({
                newPassword: "",
                confirmPassword: ""
            });
            // Set visibility based on admin status - admins see new passwords by default
            const adminVisible = currentUser?.position?.role === "admin";
            setShowPassword(adminVisible);
            setShowConfirmPassword(adminVisible);
        }
    }, [open, currentUser?.position?.role]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.newPassword || !formData.confirmPassword) {
            toast.error("Please fill in all fields");
            return;
        }

        if (formData.newPassword !== formData.confirmPassword) {
            toast.error("Passwords do not match");
            return;
        }

        if (formData.newPassword.length < 6) {
            toast.error("Password must be at least 6 characters long");
            return;
        }

        setLoading(true);

        try {
            const response = await updateUser({
                _id: staff._id,
                password: formData.newPassword
            });

            if (response?.data.RESULT === "ERROR") {
                toast.error(response.data.message);
            } else {
                toast.success("Password updated successfully!");
                setFormData({ newPassword: "", confirmPassword: "" });
                onClose();
            }
        } catch (error) {
            console.error("Error updating password:", error);
            toast.error("Failed to update password");
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setFormData({ newPassword: "", confirmPassword: "" });
        onClose();
    };

    if (!staff) return null;

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center">
                        <Key className="h-5 w-5 mr-2" />
                        Update Password
                    </DialogTitle>
                    <DialogDescription>
                        Update password for {staff.firstName} {staff.lastName || ""}
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-medium mb-2">Staff Information</h4>
                        <div className="text-sm space-y-1">
                            <p>
                                <strong>Name:</strong> {staff.firstName} {staff.lastName || ""}
                            </p>
                            <p>
                                <strong>Email:</strong> {staff.email}
                            </p>
                            <p>
                                <strong>User Name:</strong> {staff.userName || "-"}
                            </p>
                            <p>
                                <strong>Position:</strong> {staff.position.name}
                            </p>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <div>
                            <Label htmlFor="newPassword">New Password *</Label>
                            <div className="relative">
                                <Input
                                    id="newPassword"
                                    type={showPassword ? "text" : "password"}
                                    value={formData.newPassword}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, newPassword: e.target.value }))}
                                    placeholder="Enter new password"
                                    required
                                />
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </Button>
                            </div>
                        </div>

                        <div>
                            <Label htmlFor="confirmPassword">Confirm Password *</Label>
                            <div className="relative">
                                <Input
                                    id="confirmPassword"
                                    type={showConfirmPassword ? "text" : "password"}
                                    value={formData.confirmPassword}
                                    onChange={(e) =>
                                        setFormData((prev) => ({ ...prev, confirmPassword: e.target.value }))
                                    }
                                    placeholder="Confirm new password"
                                    required
                                />
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                >
                                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </Button>
                            </div>
                        </div>
                    </div>

                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                        <h4 className="font-medium text-blue-900 mb-2">Password Requirements</h4>
                        <ul className="text-sm text-blue-800 space-y-1">
                            <li>• Minimum 6 characters long</li>
                            <li>• Use a combination of letters and numbers</li>
                            <li>• Avoid using personal information</li>
                            <li>• Password will be encrypted and stored securely</li>
                        </ul>
                    </div>

                    <div className="flex space-x-3">
                        <Button type="submit" className="flex-1 " disabled={loading}>
                            {loading ? "Updating..." : "Update Password"}
                        </Button>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={handleClose}
                            disabled={loading}
                            className="flex-1"
                        >
                            Cancel
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}
